// گاوصندوق محلی حالت آفلاین (ARD-offline §۲ و §۴؛ AP-OFF-01): Snapshot و Outbox فقط رمزشده (AES-GCM، کلید غیرقابل استخراج) به ازای دامنه
// (مستأجر، کاربر). دامنه دیگر (کاربر یا مستأجر تازه) ← پاک‌سازی کامل پیش از باز شدن. Snapshot حداکثر ۴۸ ساعت؛ عملیات همگام‌شده پس از ۴۸ ساعت
// حذف می‌شوند و عملیات همگام‌نشده تا همگام‌سازی یا خروج می‌مانند (C-AGENT-1547).
import type { BilOfflineSnapshotView, BilSyncOperationInput, BilSyncOperationResult } from '@phoenix/api-client';
import { createVaultKey, isSealed, seal, unseal, type SealedRecord } from './crypto';
import type { OfflineStore } from './store';

export interface VaultScope {
  tenantId: string;
  userId: string;
}

/** بیشینه عمر Snapshot و نتایج همگام‌شده (ARD-offline §۲). */
export const SNAPSHOT_MAX_AGE_MS = 48 * 60 * 60 * 1000;

export type OutboxStatus = 'pending' | 'applied' | 'conflict' | 'resolved' | 'dismissed';

/** خلاصه نمایشی محلی عملیات (برای فهرست Outbox و رسید). */
export interface OutboxDisplay {
  patientName?: string;
  provisionalNumber?: string;
  amount?: number;
}

export interface OutboxEntry {
  operation: BilSyncOperationInput;
  status: OutboxStatus;
  createdAt: number;
  display?: OutboxDisplay;
  result?: BilSyncOperationResult;
  syncedAt?: number;
}

export interface StoredSnapshot {
  snapshot: BilOfflineSnapshotView;
  fetchedAt: number;
}

interface KeyRecord {
  scope: VaultScope;
  key: CryptoKey;
  createdAt: number;
}

interface SnapshotRecord {
  sealed: SealedRecord;
  expiresAt: number;
}

interface OutboxRecord {
  sealed: SealedRecord;
  createdAt: number;
}

const KEY = 'key';
const SNAPSHOT = 'snapshot';

function sameScope(a: VaultScope, b: VaultScope): boolean {
  return a.tenantId === b.tenantId && a.userId === b.userId;
}

export class OfflineVault {
  private constructor(
    private readonly store: OfflineStore,
    readonly scope: VaultScope,
    private readonly key: CryptoKey,
  ) {}

  /** گشودن گاوصندوق دامنه؛ دامنه دیگر یا کلید ناموجود ← پاک‌سازی و کلید تازه. */
  static async open(store: OfflineStore, scope: VaultScope, now: number = Date.now()): Promise<OfflineVault> {
    const existing = await store.get<KeyRecord>('vault', KEY);
    if (existing && sameScope(existing.scope, scope) && existing.key) {
      const vault = new OfflineVault(store, scope, existing.key);
      await vault.prune(now);
      return vault;
    }
    await OfflineVault.purge(store);
    const key = await createVaultKey();
    await store.put('vault', KEY, { scope, key, createdAt: now } satisfies KeyRecord);
    return new OfflineVault(store, scope, key);
  }

  /** پاک‌سازی کامل (خروج، جابه‌جایی مستأجر یا کاربر): کلید، Snapshot و Outbox. */
  static async purge(store: OfflineStore): Promise<void> {
    await store.clear('vault');
    await store.clear('outbox');
  }

  private aad(kind: string): string {
    return `${this.scope.tenantId}|${this.scope.userId}|${kind}`;
  }

  async saveSnapshot(snapshot: BilOfflineSnapshotView, now: number = Date.now()): Promise<void> {
    const sealed = await seal(this.key, this.aad(SNAPSHOT), { snapshot, fetchedAt: now } satisfies StoredSnapshot);
    const expiresAt = Math.min(Date.parse(snapshot.expiresAt) || now + SNAPSHOT_MAX_AGE_MS, now + SNAPSHOT_MAX_AGE_MS);
    await this.store.put('vault', SNAPSHOT, { sealed, expiresAt } satisfies SnapshotRecord);
  }

  async loadSnapshot(now: number = Date.now()): Promise<StoredSnapshot | null> {
    const record = await this.store.get<SnapshotRecord>('vault', SNAPSHOT);
    if (!record || !isSealed(record.sealed)) {
      return null;
    }
    if (record.expiresAt <= now) {
      await this.store.delete('vault', SNAPSHOT);
      return null;
    }
    return unseal<StoredSnapshot>(this.key, this.aad(SNAPSHOT), record.sealed);
  }

  async put(entry: OutboxEntry): Promise<void> {
    const id = entry.operation.clientOperationId;
    const sealed = await seal(this.key, this.aad(`outbox:${id}`), entry);
    await this.store.put('outbox', id, { sealed, createdAt: entry.createdAt } satisfies OutboxRecord);
  }

  /** همه عملیات به ترتیب ایجاد (کلید UUIDv7). */
  async entries(): Promise<OutboxEntry[]> {
    const rows = await this.store.entries<OutboxRecord>('outbox');
    const result: OutboxEntry[] = [];
    for (const row of rows) {
      if (isSealed(row.value.sealed)) {
        result.push(await unseal<OutboxEntry>(this.key, this.aad(`outbox:${row.key}`), row.value.sealed));
      }
    }
    return result.sort((a, b) => a.createdAt - b.createdAt || (a.operation.clientOperationId < b.operation.clientOperationId ? -1 : 1));
  }

  async pending(): Promise<OutboxEntry[]> {
    return (await this.entries()).filter((entry) => entry.status === 'pending');
  }

  /** حذف عملیات همگام‌شده قدیمی‌تر از ۴۸ ساعت و Snapshot منقضی. */
  async prune(now: number = Date.now()): Promise<void> {
    const rows = await this.store.entries<OutboxRecord>('outbox');
    for (const row of rows) {
      if (now - row.value.createdAt > SNAPSHOT_MAX_AGE_MS) {
        const entry = isSealed(row.value.sealed)
          ? await unseal<OutboxEntry>(this.key, this.aad(`outbox:${row.key}`), row.value.sealed).catch(() => null)
          : null;
        if (!entry || entry.status !== 'pending') {
          await this.store.delete('outbox', row.key);
        }
      }
    }
    const snapshot = await this.store.get<SnapshotRecord>('vault', SNAPSHOT);
    if (snapshot && snapshot.expiresAt <= now) {
      await this.store.delete('vault', SNAPSHOT);
    }
  }
}
